import { useEffect, useState, useRef, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlay, faPause, faClockRotateLeft } from "@fortawesome/free-solid-svg-icons";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import styles from "./styles.module.css";
import {
  createMonitoringSession,
  createMonitoringSessionOnPageClose,
} from "../../../services/patient/details";
import { getPatientLocationId } from "../../../utils/helper";
import { APP_LOGOUT_EVENT } from "../../../utils/logout";
import { REVIEW_TIMER_EVENT } from "../../../pages/patient-page/dailies/review-session/openPatient";

const DailiesOORTracker = ({
  triggerSaveSignal,
  selectedTab = "RPM",
  patientId,
  locationId,
  flow,
  onTick,
  // SCRUM-850 (review session): { get, hold, release } for the count kept
  // while the session is paused, so continuing it - here or in another
  // browser tab - starts from there. That part is already saved; only the
  // seconds after it are.
  heldTimer,
  // The note save behind triggerSaveSignal already saved the timer's time,
  // so the signal only resets the timer (saving again counted it twice).
  noteSavesTime = false,
}) => {
  const queryClient = useQueryClient();
  const [initialSeconds] = useState(() => heldTimer?.get() || 0);
  const [isTracking, setIsTracking] = useState(true);
  const [rpmTime, setRPMTime] = useState(initialSeconds);
  // Part of rpmTime that is already saved.
  const savedBaseRef = useRef(initialSeconds);
  const heldTimerRef = useRef(heldTimer);
  heldTimerRef.current = heldTimer;
  const onTickRef = useRef(onTick);
  onTickRef.current = onTick;
  // True while the review session holds the timer paused.
  const sessionPausedRef = useRef(false);
  // ...and it was running then (not the user's own Pause), so Resume
  // Session restarts it.
  const heldBySessionRef = useRef(false);
  const startTimeRef = useRef(null);
  const intervalRef = useRef(null);
  const previousPatientIdRef = useRef(patientId);

  // Ref mirrors rpmTime so event listeners/cleanup always read fresh value
  const rpmTimeRef = useRef(0);
  rpmTimeRef.current = rpmTime;

  // Ref to track isTracking for synchronous reads in start/pause functions
  const isTrackingRef = useRef(true);
  isTrackingRef.current = isTracking;

  // Flag to prevent auto-restart after manual save/pause
  const manualStopRef = useRef(false);

  // Store the elapsed time under THIS patient's own location, not the viewer's
  // current location (SCRUM-157). Held in a ref kept in lockstep with the
  // locationId prop so the auto-save (which fires on unmount, when the parent
  // may already be switching patients) uses the right patient's location.
  // Falls back to getPatientLocationId() (which reads the captured _pt_loc_id,
  // then current_loc) when no prop is supplied, so saves never break.
  const locationIdRef = useRef(locationId);
  useEffect(() => {
    if (locationId != null) locationIdRef.current = locationId;
  }, [locationId]);

  const mutation = useMutation(
    (sessionData) =>
      createMonitoringSession(
        sessionData,
        previousPatientIdRef.current,
        locationIdRef.current ?? getPatientLocationId()
      ),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["care-manager-summary"]);
      },
      onError: () => {},
    }
  );

  // Match the patient-details header pill: minute-scale MM:SS with no
  // hours segment (90 minutes reads 90:00, not 01:30:00).
  const formatTime = (seconds) => {
    const m = String(Math.floor(seconds / 60)).padStart(2, "0");
    const s = String(seconds % 60).padStart(2, "0");
    return `${m}:${s}`;
  };

  const startTracking = () => {
    // Clear manual stop flag when user explicitly clicks Play
    manualStopRef.current = false;

    // Clear any existing interval first to prevent duplicates
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    setIsTracking(true);
    startTimeRef.current = new Date().toISOString();
    intervalRef.current = setInterval(() => {
      setRPMTime((prev) => prev + 1);
    }, 1000);
  };

  const pauseTracking = () => {
    if (isTrackingRef.current) {
      manualStopRef.current = true;
      setIsTracking(false);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }
  };

  // Discard — stops the clock, resets to 0, does NOT save.
  // User must press Play manually to start again.
  const discardAndStop = () => {
    manualStopRef.current = true;
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setIsTracking(false);
    setRPMTime(0);
    rpmTimeRef.current = 0;
    savedBaseRef.current = 0;
    startTimeRef.current = null;
  };

  // The unsaved part of rpmTime as a monitoring-session payload (null when
  // there's nothing to save). Marks it saved; `restore` puts it back.
  const takeUnsaved = () => {
    const currentTime = rpmTimeRef.current - savedBaseRef.current;
    if (!startTimeRef.current || currentTime <= 0) return null;
    const base = savedBaseRef.current;
    savedBaseRef.current = rpmTimeRef.current;
    return {
      payload: {
        monitoring_seconds: currentTime,
        total_rpm_seconds: currentTime,
        ...(flow === "Dailies"
          ? { dailies_flow: true }
          : { out_of_range_flow: true }),
      },
      restore: () => {
        savedBaseRef.current = Math.min(savedBaseRef.current, base);
      },
    };
  };

  // Save — only called by the legitimate triggers below
  const saveSession = useCallback(() => {
    const unsaved = takeUnsaved();
    if (!unsaved) return;
    // A failed save stays unsaved, so the next save includes it.
    mutation.mutate(unsaved.payload, { onError: unsaved.restore });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flow]);

  // Page close / refresh / logout: the request has to outlive the page.
  const saveOnPageClose = useCallback(() => {
    const unsaved = takeUnsaved();
    if (!unsaved) return;
    createMonitoringSessionOnPageClose(
      unsaved.payload,
      previousPatientIdRef.current,
      locationIdRef.current ?? getPatientLocationId()
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flow]);

  // ── Start interval when component mounts / patientId changes ────────────
  useEffect(() => {
    // Clear any existing interval first
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    // Reset manual stop flag on patient change (fresh start)
    manualStopRef.current = false;

    setIsTracking(true);
    setRPMTime(initialSeconds);
    startTimeRef.current = new Date().toISOString();
    intervalRef.current = setInterval(() => {
      setRPMTime((prev) => prev + 1);
    }, 1000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
    // initialSeconds only applies when the timer starts for a patient.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId]);

  // ── 2. Save on triggerSaveSignal (next/previous patient navigation OR manual save)
  //    Resets timer to 0 AND stops tracking — matches Patient Dashboard behavior
  useEffect(() => {
    if (triggerSaveSignal) {
      // Clear interval immediately
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }

      if (!noteSavesTime) saveSession();
      heldTimerRef.current?.release();
      setRPMTime(0);
      savedBaseRef.current = 0;
      setIsTracking(false); // Stop tracking after manual save
      manualStopRef.current = true; // Mark as manually stopped
      previousPatientIdRef.current = patientId;
      startTimeRef.current = new Date().toISOString();
    }
  }, [triggerSaveSignal]);

  // ── 3. Save on component unmount (leaving the Dailies tab / page) ──────────
  useEffect(() => {
    return () => {
      // Keep the count only for a session that's paused on this patient.
      if (!sessionPausedRef.current) heldTimerRef.current?.release();
      saveSession();
    };
  }, []);

  // ── 3b. Pause / resume with the review session (SCRUM-850) ─────────────────
  useEffect(() => {
    const handleReviewTimer = (event) => {
      const { action, rewindSeconds = 0 } = event.detail || {};
      if (action === "pause") {
        if (isTrackingRef.current) {
          heldBySessionRef.current = true;
          pauseTracking();
          // Idle pause: stop at the last activity, so idle time isn't
          // counted (never below what's already saved).
          if (rewindSeconds > 0) {
            const value = Math.max(savedBaseRef.current, rpmTimeRef.current - rewindSeconds);
            rpmTimeRef.current = value;
            setRPMTime(value);
          }
        }
        sessionPausedRef.current = true;
        heldTimerRef.current?.hold(rpmTimeRef.current);
        // Save what's recorded so far, so leaving, refreshing or losing the
        // connection while paused can't lose or change it.
        saveSession();
        reportTick();
      } else if (action === "resume") {
        sessionPausedRef.current = false;
        // Another browser tab may have carried on with this patient (and
        // saved that time) while this one was paused.
        const held = heldTimerRef.current?.get() || 0;
        if (held > rpmTimeRef.current) {
          rpmTimeRef.current = held;
          savedBaseRef.current = held;
          setRPMTime(held);
        }
        if (heldBySessionRef.current) {
          heldBySessionRef.current = false;
          startTracking();
        }
      } else if (action === "reset") {
        // The time was saved in another browser tab (a note there): back to
        // 00:00 and stopped, as it is there.
        heldBySessionRef.current = false;
        discardAndStop();
        reportTick();
      }
    };
    window.addEventListener(REVIEW_TIMER_EVENT, handleReviewTimer);
    return () => window.removeEventListener(REVIEW_TIMER_EVENT, handleReviewTimer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── 4. Save on browser tab close / page refresh ────────────────────────────
  //    (also logout, which clears the token before the page goes away)
  useEffect(() => {
    window.addEventListener("beforeunload", saveOnPageClose);
    window.addEventListener(APP_LOGOUT_EVENT, saveOnPageClose);
    return () => {
      window.removeEventListener("beforeunload", saveOnPageClose);
      window.removeEventListener(APP_LOGOUT_EVENT, saveOnPageClose);
    };
  }, [saveOnPageClose]);

  // ── Notify parent of timer updates ───────────────────────────────────────────
  //    The time not saved yet - what a note saved now should record.
  function reportTick() {
    onTickRef.current?.({ elapsedTimeRPM: rpmTimeRef.current - savedBaseRef.current, elapsedTimeCCM: 0 });
  }
  useEffect(() => {
    reportTick();
  }, [rpmTime]);

  return (
    <div className={styles.trackerWrapper}>

      {/* 1 — Pause / Play button (leftmost) — matches Figma order */}
      <button
        className={styles.trackerPauseBtn}
        onClick={() => (isTracking ? pauseTracking() : startTracking())}
        aria-label={isTracking ? "Pause" : "Resume"}
        title={isTracking ? "Pause" : "Resume"}
      >
        <FontAwesomeIcon icon={isTracking ? faPause : faPlay} />
      </button>

      {/* 2 — Gray pill: "03:37 RPM" (center) — matches Figma */}
      <div className={styles.trackerPill}>
        <span className={styles.trackerTime}>{formatTime(rpmTime)}</span>
        <span className={styles.trackerLabel}>RPM</span>
      </div>

      {/* 3 — Discard button — stops + resets timer WITHOUT saving.
              User must press Play to start tracking again. */}
      <button
        className={styles.trackerHistoryBtn}
        onClick={discardAndStop}
        aria-label="Discard and stop timer"
        title="Discard and stop timer"
      >
        <FontAwesomeIcon icon={faClockRotateLeft} />
      </button>

    </div>
  );
};

export default DailiesOORTracker;